import type { AffineEditorContainer } from '@affine/core/blocksuite/block-suite-editor';
import { insertFromMarkdown } from '@affine/core/blocksuite/utils/markdown-utils';
import { getSurfaceBlock } from '@blocksuite/affine/blocks/surface';
import { Bound } from '@blocksuite/affine/global/gfx';
import { NoteDisplayMode } from '@blocksuite/affine/model';
import type { Store } from '@blocksuite/affine/store';

import type { CuriesciousActionDetail, MindmapNodeType } from '../types';
import {
  buildCardMarkdown,
  CARD_H,
  CARD_W,
  findCardNotes,
  nextFreshPosition,
  NODE_TYPE_REGISTRY,
  TYPE_BACKGROUND,
} from './canvas-helpers';
import {
  applySourceLink,
  removeSourceLink,
  sourceHref,
} from './source-link-format';

/**
 * BlockSuite default for the doc's primary note. When the doc is
 * viewed in edgeless mode, this note shows up AS A CARD on the
 * canvas — which means "select all + delete" on the canvas wipes the
 * doc's content. We flip it to DocOnly on the first card add so the
 * canvas only contains our captured cards; the doc text stays
 * intact in page mode where the user reads.
 */
type FlippableNote = {
  id: string;
  flavour?: string;
  props?: { displayMode?: NoteDisplayMode };
};

function isolateMainNoteFromCanvas(doc: {
  root: { children: unknown[] } | null;
  updateBlock: (model: unknown, props: Record<string, unknown>) => void;
}): string[] {
  const flipped: string[] = [];
  const root = doc.root;
  if (!root) return flipped;
  for (const child of root.children) {
    const node = child as FlippableNote;
    if (node.flavour !== 'affine:note') continue;
    if (node.props?.displayMode === NoteDisplayMode.DocAndEdgeless) {
      try {
        doc.updateBlock(node, { displayMode: NoteDisplayMode.DocOnly });
        flipped.push(node.id);
      } catch {
        /* ignore */
      }
    }
  }
  return flipped;
}

function restoreMainNoteToBothModes(
  doc: {
    getBlock: (id: string) => { model?: unknown } | undefined;
    updateBlock: (model: unknown, props: Record<string, unknown>) => void;
  },
  noteIds: string[]
): void {
  for (const id of noteIds) {
    try {
      const note = doc.getBlock(id)?.model;
      if (note) {
        doc.updateBlock(note, {
          displayMode: NoteDisplayMode.DocAndEdgeless,
        });
      }
    } catch {
      /* ignore */
    }
  }
}

/**
 * One-time migration: remove any leftover `mindmap` surface elements
 * left behind by the previous mindmap-tree code path. BlockSuite's
 * `MindmapElementModel._layout` crashes (`stashTree → traverse`
 * dereffs null) when a mindmap gets caught in a bulk select-all-delete
 * on the canvas — and even our card-only "Clear" flow would trip it
 * if a stale mindmap is still sitting on the surface.
 *
 * Removing them up-front, in a single transaction, sidesteps the
 * cascading-delete that triggers the bad layout pass. Idempotent: on
 * a clean doc this is a no-op.
 */
function sweepLegacyMindmaps(doc: Store): number {
  const surface = getSurfaceBlock(doc);
  if (!surface) return 0;
  const mindmaps = surface.elementModels.filter(
    el => (el as { type?: string }).type === 'mindmap'
  );
  if (mindmaps.length === 0) return 0;
  let removed = 0;
  // Single transaction so BlockSuite doesn't queue a layout pass
  // between deletes — the layout is what crashes mid-traversal.
  doc.transact(() => {
    for (const mm of mindmaps) {
      try {
        surface.deleteElement(mm.id);
        removed += 1;
      } catch (err) {
        console.warn(
          '[add-to-canvas] failed to remove legacy mindmap element',
          (mm as { id?: string }).id,
          err
        );
      }
    }
  });
  return removed;
}

/**
 * Toggle the doc out of user-readonly for the duration of `fn`. The
 * research doc is flipped to readonly while the agent streams its
 * response — without this wrapper any surface mutation throws
 * "Cannot add element in readonly mode". Mirrors the pattern the
 * centered-chat doc-writer uses.
 */
async function runWithWrites<T>(
  doc: { readonly?: boolean },
  fn: () => Promise<T> | T
): Promise<T> {
  const wasReadonly = doc.readonly === true;
  if (wasReadonly) doc.readonly = false;
  try {
    return await fn();
  } finally {
    if (wasReadonly) doc.readonly = true;
  }
}

export interface AddCardResult {
  /** Id of the affine:note block we created on the surface. */
  noteId: string;
  /** Resolved node type from the picker (for the toast wording). */
  nodeType: MindmapNodeType;
  /** Undo handle — removes the note block. */
  undo: () => void;
}

/**
 * Append the user's selected passage as a rich card on the current
 * doc's edgeless surface. The card is an `affine:note` with
 * `displayMode: EdgelessOnly` so it stays invisible during page-mode
 * reading and only surfaces when the user switches to edgeless to
 * synthesise.
 *
 * The card carries a type-tagged markdown body (type chip · quote ·
 * source line · empty scratch area). Position is the next slot in the
 * fresh-captures column — the user drags cards out of the column to
 * cluster them, and subsequent adds keep landing where they expect.
 */
export async function addCardToCanvas(
  editor: AffineEditorContainer | null,
  detail: CuriesciousActionDetail,
  nodeType: MindmapNodeType
): Promise<AddCardResult> {
  if (!editor?.host?.std?.store) {
    throw new Error('No active editor — open a doc before adding to canvas.');
  }
  const doc = editor.host.std.store;
  if (!doc.root) {
    throw new Error('Active doc has no root block — cannot add a card.');
  }
  const host = editor.host;
  const meta = NODE_TYPE_REGISTRY[nodeType];
  const sourceTitle = (
    doc as { meta?: { title?: string } }
  ).meta?.title?.trim();
  const docId = (doc as { id?: string }).id;
  // The back-link in the card body only needs docId + blockId. The
  // source-side highlight (below) is what needs `sourceRange`; we
  // keep them independent so the card always has a clickable
  // "Source ↗" even when the toolbar couldn't capture a precise
  // inline range (multi-block selections, etc.).
  const backHref =
    docId && detail.sourceBlockId
      ? sourceHref(docId, detail.sourceBlockId)
      : undefined;
  console.debug('[add-to-canvas] dispatching:', {
    nodeType,
    hasBackHref: !!backHref,
    hasSourceRange: !!detail.sourceRange,
    sourceBlockId: detail.sourceBlockId,
  });

  const markdown = buildCardMarkdown({
    typeLabel: meta.label,
    passage: detail.text,
    sourceTitle,
    sourceHref: backHref,
    citationTokens: detail.citationTokens,
  });

  const position = nextFreshPosition(doc);
  const xywh = new Bound(
    position.x,
    position.y,
    CARD_W,
    CARD_H
  ).serialize();

  // If this is the very first card on the canvas, the main doc note
  // is still visible in edgeless mode (BlockSuite default). Hide it
  // now so the canvas is exclusively "our cards" — a user who later
  // hits "Clear" on the canvas won't accidentally wipe the doc.
  // Existing cards present? Then the main note has already been
  // flipped on an earlier add — leave it alone.
  const existingCards = findCardNotes(doc);
  const shouldIsolateMainNote = existingCards.length === 0;

  let flippedMainNoteIds: string[] = [];
  const noteId = await runWithWrites(
    doc as unknown as { readonly?: boolean },
    async () => {
      if (shouldIsolateMainNote) {
        flippedMainNoteIds = isolateMainNoteFromCanvas(
          doc as unknown as Parameters<typeof isolateMainNoteFromCanvas>[0]
        );
        if (flippedMainNoteIds.length > 0) {
          console.log(
            '[add-to-canvas] isolated main note(s) from canvas:',
            flippedMainNoteIds
          );
        }
        // Migrate away from any leftover mindmap elements before they
        // can get caught in a future canvas-wide delete (which crashes
        // BlockSuite's MindmapElementModel layout). One-time, idempotent.
        const removed = sweepLegacyMindmaps(doc as Store);
        if (removed > 0) {
          console.log(
            '[add-to-canvas] swept',
            removed,
            'legacy mindmap element(s) off the canvas'
          );
        }
      }
      let createdId: string | undefined;
      doc.transact(() => {
        createdId = doc.addBlock(
          'affine:note' as never,
          {
            xywh,
            displayMode: NoteDisplayMode.EdgelessOnly,
            background: TYPE_BACKGROUND[nodeType],
          },
          doc.root!.id
        );
      });
      if (!createdId) {
        throw new Error('Failed to create the canvas card block.');
      }
      // Populate the note's body with the typed markdown. Done outside
      // the transact so the markdown adapter can run its own snapshot
      // application — it manages its own CRDT writes.
      await insertFromMarkdown(host, markdown, doc, createdId, 0);
      // Paint the source highlight on the original passage so the
      // researcher sees at a glance that this paragraph has a card on
      // the canvas. Skipped when the toolbar couldn't capture a
      // single-block inline range (multi-block selections etc.).
      if (detail.sourceBlockId && detail.sourceRange) {
        applySourceLink(
          doc,
          detail.sourceBlockId,
          detail.sourceRange,
          createdId,
          nodeType
        );
      }
      return createdId;
    }
  );

  const undo = () => {
    runWithWrites(doc as unknown as { readonly?: boolean }, () => {
      // Clear the source-side highlight first so the doc doesn't keep
      // a dangling link to a non-existent card.
      if (detail.sourceBlockId && detail.sourceRange) {
        removeSourceLink(doc, detail.sourceBlockId, detail.sourceRange);
      }
      try {
        const note = doc.getBlock(noteId)?.model;
        if (note) doc.deleteBlock(note);
      } catch {
        /* already gone */
      }
      // If THIS add was the one that flipped the main note off the
      // canvas, restore it so the doc looks the same as before.
      if (flippedMainNoteIds.length > 0) {
        restoreMainNoteToBothModes(
          doc as unknown as Parameters<typeof restoreMainNoteToBothModes>[0],
          flippedMainNoteIds
        );
      }
    }).catch(err => {
      console.warn('[add-to-canvas] undo failed:', err);
    });
  };

  return { noteId, nodeType, undo };
}
